/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 *  ******************************************************************************
 */

import { createFeature, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Cluster, Message } from 'CalltakingCoreApi';
import {
    fetchDtmfMessagesSuccess,
    fetchPredefinedSmsMessagesSuccess,
    fetchPredefinedTddMessagesSuccess,
    fetchRttMessagesSuccess,
    fetchSmsMessagesSuccess,
    fetchTddMessagesSuccess,
    fetchTextCallHistorySuccess,
    fetchTranslationLanguagesFail,
    fetchTranslationLanguagesSuccess,
    messageEffectsInitialized,
    newDtmfMessage,
    newRttMessage,
    newSmsMessage,
    newTddMessage,
    purgeMessages,
    updateDtmfMessage,
    updateMessageSubscriptions,
    updateMultipleMessageSubscriptions,
    updateRttMessage,
    updateSmsMessage,
    updateTddMessage
} from './message.actions';
import { SortFunctions } from '../../call/util/sort-functions';
import { PredefinedMessages } from '../model/pre-defined-messages';
import { HistoricalTextMessage } from '../model/historical-text-message';
import { initSubscriptionState } from '../../configuration/+state/configuration.actions';
import { MultipleSubscriptionsStatus, Topic } from '../../user/model/multiple-subscriptions';
import { TranslationLanguage } from '../model/translation.model';

export interface MessageState {
    subscriptions: { [topic: string]: { [receipt: string]: boolean } };
    multipleSubscriptions: MultipleSubscriptionsStatus;
    initialized: boolean;
    sms: SMSMessageState;
    tdd: TDDMessageState;
    rtt: RTTMessageState;
    dtmf: DTMFMessageState;
    smsCallMap: { [callId: string]: { [messageId: string]: Message } };
    tddCallMap: { [callId: string]: { [messageId: string]: Message } };
    rttCallMap: { [callId: string]: { [messageId: string]: Message } };
    dtmfCallMap: { [callId: string]: { [messageId: string]: Message } };
    historicalTextMap: { [ref: string]: { [callId: string]: Message[] } };
    default: DefaultMessageState;
    predefinedSms: PredefinedMessages[];
    predefinedTdd: PredefinedMessages[];
    smsHistorical: SMSHistoricalMessageState;
    translationLanguages: TranslationLanguage[];
}

export interface SMSMessageState extends EntityState<Message> {}

export interface TDDMessageState extends EntityState<Message> {}

export interface RTTMessageState extends EntityState<Message> {}

export interface DTMFMessageState extends EntityState<Message> {}

export interface DefaultMessageState extends EntityState<Message> {}

export interface SMSHistoricalMessageState extends EntityState<HistoricalTextMessage> {}

export const smsAdapter: EntityAdapter<Message> = createEntityAdapter<Message>({
    selectId: (message: Message) => message.uuid,
    sortComparer: SortFunctions.newestCreatedSort
});

export const tddAdapter: EntityAdapter<Message> = createEntityAdapter<Message>({
    selectId: (message: Message) => message.uuid,
    sortComparer: SortFunctions.newestCreatedSort
});

export const rttAdapter: EntityAdapter<Message> = createEntityAdapter<Message>({
    selectId: (message: Message) => message.uuid,
    sortComparer: SortFunctions.newestCreatedSort
});

export const dtmfAdapter: EntityAdapter<Message> = createEntityAdapter<Message>({
    selectId: (message: Message) => message.uuid,
    sortComparer: SortFunctions.newestCreatedSort
});

export const defaultAdapter: EntityAdapter<Message> = createEntityAdapter<Message>({
    selectId: (message: Message) => message.uuid,
    sortComparer: SortFunctions.newestCreatedSort
});

export const smsHistoricalAdapter: EntityAdapter<HistoricalTextMessage> = createEntityAdapter<HistoricalTextMessage>({
    selectId: (message: HistoricalTextMessage) => message.uuid
});

enum MessageKey {
    SMS = 'sms',
    TDD = 'tdd',
    RTT = 'rtt',
    DTMF = 'dtmf'
}

enum CallMapKey {
    SMS = 'smsCallMap',
    TDD = 'tddCallMap',
    RTT = 'rttCallMap',
    DTMF = 'dtmfCallMap'
}

const initialSmsState: SMSMessageState = smsAdapter.getInitialState({});

const initialTddState: TDDMessageState = tddAdapter.getInitialState({});

const initialRttState: TDDMessageState = rttAdapter.getInitialState({});

const initialDtmfState: DTMFMessageState = dtmfAdapter.getInitialState({});

const initialDefaultState: DefaultMessageState = defaultAdapter.getInitialState({});

const initialSmsHistoricalState: SMSHistoricalMessageState = smsHistoricalAdapter.getInitialState({});

const initialState: MessageState = {
    subscriptions: {},
    multipleSubscriptions: {},
    initialized: false,
    sms: initialSmsState,
    tdd: initialTddState,
    rtt: initialRttState,
    dtmf: initialDtmfState,
    smsCallMap: {},
    tddCallMap: {},
    rttCallMap: {},
    dtmfCallMap: {},
    historicalTextMap: {},
    default: initialDefaultState,
    predefinedSms: [],
    predefinedTdd: [],
    smsHistorical: initialSmsHistoricalState,
    translationLanguages: []
};

function handleMessage(
    state: MessageState,
    message: Message,
    adapter: EntityAdapter<Message>,
    stateKey: MessageKey,
    callMapKey: CallMapKey
): MessageState {
    return {
        ...state,
        [stateKey]: adapter.setOne(message, state[stateKey]),
        [callMapKey]: { ...state[callMapKey], [message.callId]: { ...state[callMapKey][message.callId], [message.uuid]: message } }
    };
}

export const messageFeature = createFeature({
    name: 'messages',
    reducer: createReducer(
        initialState,
        on(messageEffectsInitialized, (state): MessageState => {
            return { ...state, initialized: true };
        }),
        on(updateMessageSubscriptions, (state, { subscriptions }): MessageState => {
            return { ...state, subscriptions: { ...state.subscriptions, ...subscriptions } };
        }),
        on(initSubscriptionState, (state, { config }) => {
            const multipleSubscriptions: MultipleSubscriptionsStatus = {};
            config.clusters.forEach((c: Cluster) => {
                multipleSubscriptions[c.name] = {
                    [Topic.MESSAGES_DTMF_NEW]: false,
                    [Topic.MESSAGES_DTMF_EDIT]: false,
                    [Topic.MESSAGES_DTMF_DELETE]: false,
                    [Topic.MESSAGES_RTT_NEW]: false,
                    [Topic.MESSAGES_RTT_EDIT]: false,
                    [Topic.MESSAGES_RTT_DELETE]: false,
                    [Topic.MESSAGES_SMS_NEW]: false,
                    [Topic.MESSAGES_SMS_EDIT]: false,
                    [Topic.MESSAGES_SMS_DELETE]: false,
                    [Topic.MESSAGES_TDD_NEW]: false,
                    [Topic.MESSAGES_TDD_EDIT]: false,
                    [Topic.MESSAGES_TDD_DELETE]: false,
                    [Topic.MESSAGES_SYSTEM_ERROR_NEW]: false,
                    [Topic.MESSAGES_SYSTEM_ERROR_EDIT]: false,
                    [Topic.MESSAGES_SYSTEM_ERROR_DELETE]: false
                };
            });
            return {...state, multipleSubscriptions};
        }),
        on(updateMultipleMessageSubscriptions, (state, { topic, clusterName, status }) => {
            const currentSubscriptions = state.multipleSubscriptions;
            const subscriptions = {
                ...currentSubscriptions,
                [clusterName]: {
                    ...currentSubscriptions[clusterName],
                    [topic]: status
                }
            };
            return { ...state,  multipleSubscriptions: subscriptions };
        }),
        on(fetchSmsMessagesSuccess, (state, { messages }) => {
            return {
                ...state,
                sms: smsAdapter.upsertMany(messages, state.sms),
                smsCallMap: messages.reduce<{ [callId: string]: { [messageId: string]: Message } }>(
                    (map, message) => ({ ...map, [message.callId]: { ...map[message.callId], [message.uuid]: message } }),
                    { ...state.smsCallMap }
                )
            };
        }),
        on(fetchTddMessagesSuccess, (state, { messages }) => {
            return {
                ...state,
                tdd: tddAdapter.upsertMany(messages, state.tdd),
                tddCallMap: messages.reduce<{ [callId: string]: { [messageId: string]: Message } }>(
                    (map, message) => ({ ...map, [message.callId]: { ...map[message.callId], [message.uuid]: message } }),
                    { ...state.tddCallMap }
                )
            };
        }),
        on(fetchRttMessagesSuccess, (state, { messages }) => {
            return {
                ...state,
                rtt: rttAdapter.upsertMany(messages, state.rtt),
                rttCallMap: messages.reduce<{ [callId: string]: { [messageId: string]: Message } }>(
                    (map, message) => ({ ...map, [message.callId]: { ...map[message.callId], [message.uuid]: message } }),
                    { ...state.rttCallMap }
                )
            };
        }),
        on(fetchDtmfMessagesSuccess, (state, { messages }) => {
            return {
                ...state,
                dtmf: dtmfAdapter.upsertMany(messages, state.dtmf),
                dtmfCallMap: messages.reduce<{ [callId: string]: { [messageId: string]: Message } }>(
                    (map, message) => ({ ...map, [message.callId]: { ...map[message.callId], [message.uuid]: message } }),
                    { ...state.dtmfCallMap }
                )
            };
        }),
        on(newSmsMessage, (state, { message }) => handleMessage(state, message, smsAdapter, MessageKey.SMS, CallMapKey.SMS)),
        on(updateSmsMessage, (state, { message }) => handleMessage(state, message, smsAdapter, MessageKey.SMS, CallMapKey.SMS)),
        on(newTddMessage, (state, { message }) => handleMessage(state, message, tddAdapter, MessageKey.TDD, CallMapKey.TDD)),
        on(updateTddMessage, (state, { message }) => handleMessage(state, message, tddAdapter, MessageKey.TDD, CallMapKey.TDD)),
        on(newRttMessage, (state, { message }) => handleMessage(state, message, rttAdapter, MessageKey.RTT, CallMapKey.RTT)),
        on(updateRttMessage, (state, { message }) => handleMessage(state, message, rttAdapter, MessageKey.RTT, CallMapKey.RTT)),
        on(newDtmfMessage, (state, { message }) => handleMessage(state, message, dtmfAdapter, MessageKey.DTMF, CallMapKey.DTMF)),
        on(updateDtmfMessage, (state, { message }) => handleMessage(state, message, dtmfAdapter, MessageKey.DTMF, CallMapKey.DTMF)),
        on(fetchTextCallHistorySuccess, (state, { referenceKey, messages }): MessageState => {
            return {
                ...state,
                historicalTextMap: messages.reduce<{ [ref: string]: { [callId: string]: Message[] } }>(
                    (map, message) => ({
                        ...map,
                        [referenceKey]: {
                            ...map[referenceKey],
                            [message.callId]:
                                map[referenceKey] && map[referenceKey][message.callId] ? [...map[referenceKey][message.callId]].concat(message) : [message]
                        }
                    }),
                    {}
                )
            };
        }),
        on(purgeMessages, (state, { uuids }) => {
            let smsCallMap = { ...state.smsCallMap };
            uuids.forEach((uuid) => delete smsCallMap[uuid]);
            let tddCallMap = { ...state.tddCallMap };
            uuids.forEach((uuid) => delete tddCallMap[uuid]);
            let rttCallMap = { ...state.rttCallMap };
            uuids.forEach((uuid) => delete rttCallMap[uuid]);
            let dtmfCallMap = { ...state.dtmfCallMap };
            uuids.forEach((uuid) => delete dtmfCallMap[uuid]);

            return {
                ...state,
                sms: smsAdapter.removeMany((message) => uuids.includes(message.callId), state.sms),
                tdd: tddAdapter.removeMany((message) => uuids.includes(message.callId), state.tdd),
                rtt: rttAdapter.removeMany((message) => uuids.includes(message.callId), state.rtt),
                dtmf: dtmfAdapter.removeMany((message) => uuids.includes(message.callId), state.dtmf),
                default: defaultAdapter.removeMany((message) => uuids.includes(message.callId), state.default),
                smsCallMap,
                tddCallMap,
                rttCallMap,
                dtmfCallMap
            };
        }),
        on(fetchPredefinedSmsMessagesSuccess, (state, { messages }): MessageState => {
            return { ...state, predefinedSms: messages };
        }),
        on(fetchPredefinedTddMessagesSuccess, (state, { messages }): MessageState => {
            return { ...state, predefinedTdd: messages };
        }),
        on(fetchTranslationLanguagesSuccess, (state, { languages }): MessageState => {
            return { ...state, translationLanguages: languages };
        }),
        on(fetchTranslationLanguagesFail, (state): MessageState => {
            console.warn('reducer fetchTranslationLanguagesFail — translationLanguages reset to []');
            return { ...state, translationLanguages: [] };
        }),
    )
});

export const selectSmsState = (state: MessageState) => state.sms;
export const selectTddState = (state: MessageState) => state.tdd;
export const selectDtmfState = (state: MessageState) => state.dtmf;
export const selectDefaultState = (state: MessageState) => state.default;

export const { selectAll: selectAllSms } = smsAdapter.getSelectors();
export const { selectAll: selectAllTdd } = tddAdapter.getSelectors();
export const { selectAll: selectAllDtmf } = dtmfAdapter.getSelectors();
export const { selectAll: selectAllDefault } = defaultAdapter.getSelectors();
